/**
 * Push prompt telemetry.
 *
 * Every time the browser notification prompt is attempted we log what
 * happened (prompted / granted / denied / dismissed / error), on which
 * route, and how long after landing it fired. Written with the
 * service-role client because the table is admin-read-only.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const EventSchema = z.object({
  event: z.enum(["prompted", "granted", "denied", "dismissed", "error", "unsupported"]),
  source: z.string().max(40).optional(),
  route: z.string().max(300).optional(),
  anonymousId: z.string().max(64).optional(),
  timeToPromptMs: z.number().int().min(0).max(24 * 60 * 60 * 1000).optional(),
  userAgent: z.string().max(400).optional(),
});

export type RecordPushPromptEventInput = z.input<typeof EventSchema>;

export const recordPushPromptEvent = createServerFn({ method: "POST" })
  .inputValidator((data: RecordPushPromptEventInput) => EventSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("push_prompt_events" as never).insert({
      event: data.event,
      source: data.source ?? null,
      route: data.route ?? null,
      anonymous_id: data.anonymousId ?? null,
      time_to_prompt_ms: data.timeToPromptMs ?? null,
      user_agent: data.userAgent ?? null,
    } as never);
    if (error) return { ok: false };
    return { ok: true };
  });
